/**
 * QR-пропуск (референс QrPassModal): настоящий QR со ссылкой на цель по
 * URL-контракту `/v/{venue}?to={id}` — отсканировал → открылась карта с целью.
 * QR рендерит пакет qrcode (lazy, уже используется открыткой машины).
 */
import { getLang, tr } from '@app/i18n'
import { localized } from '@app/rvf'
import { useEffect, useState } from 'preact/hooks'
import { importChunk } from '../lazy-retry'
import { langTick, qrPassTargetId, venue } from '../state'

/**
 * Особая цель — САМА ПЛОЩАДКА (запрос владельца 30.07): QR ведёт на карту
 * территории без выбранной цели, `/v/{venue}` по URL-контракту (ADR 0004).
 * Отдельный сигнал заводить незачем — модал и так умеет ровно это, меняется
 * только ссылка и заголовок.
 */
export const VENUE_QR_TARGET = '@venue'

function targetName(id: string): string {
  const v = venue.value
  if (!v) return id
  if (id === VENUE_QR_TARGET) return localized(v.venue.name, getLang(), v.venue.default_language)
  const unit = v.units.find((u) => u.id === id)
  if (unit) return localized(unit.name, getLang(), v.venue.default_language)
  const poi = v.pois.find((p) => p.id === id)
  if (poi) return localized(poi.name, getLang(), v.venue.default_language)
  return id
}

export function QrPassModal() {
  langTick.value
  const id = qrPassTargetId.value
  const v = venue.value
  const [dataUrl, setDataUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const venueUrl = v ? `${location.origin}/v/${encodeURIComponent(v.venue.id)}` : ''
  const link =
    !id || !v ? '' : id === VENUE_QR_TARGET ? venueUrl : `${venueUrl}?to=${encodeURIComponent(id)}`

  useEffect(() => {
    setDataUrl(null)
    setCopied(false)
    if (!link) return
    void importChunk(() => import('qrcode')).then(async (qrcode) => {
      const url = await qrcode.toDataURL(link, { width: 480, margin: 1 })
      setDataUrl(url)
    })
  }, [link])

  // Esc закрывает модал (клавиатурный аналог клика по фону).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') qrPassTargetId.value = null
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (!id || !v) return null

  const close = (): void => {
    qrPassTargetId.value = null
  }

  const copy = (): void => {
    void navigator.clipboard?.writeText(link).then(() => setCopied(true))
  }

  const share = (): void => {
    if (navigator.share) {
      void navigator.share({ title: targetName(id), url: link }).catch(() => undefined)
    } else {
      window.print()
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: клик по фону — вторичный жест закрытия; клавиатура — Esc и кнопка ✕
    <div class="qrpass-backdrop" onClick={close}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation, не действие */}
      <div class="qrpass glass anim-toast" onClick={(e) => e.stopPropagation()}>
        <div class="qrpass-head">
          <div>
            <p class="tech-label qrpass-kicker">
              {tr(id === VENUE_QR_TARGET ? 'qrpass.kicker_venue' : 'qrpass.kicker')}
            </p>
            <h2 class="qrpass-title">{targetName(id)}</h2>
          </div>
          <button type="button" class="icon-btn" aria-label={tr('common.close')} onClick={close}>
            ✕
          </button>
        </div>
        <div class="qrpass-card">
          {dataUrl ? (
            <img class="qrpass-img" src={dataUrl} alt={tr('qrpass.alt')} />
          ) : (
            <div class="qrpass-img qrpass-loading" aria-busy="true" />
          )}
          <p class="tech-label qrpass-scan">{tr('qrpass.scan_hint')}</p>
        </div>
        <div class="qrpass-actions">
          <button type="button" class="measure-btn" onClick={copy}>
            {copied ? tr('qrpass.copied') : tr('qrpass.copy')}
          </button>
          <button type="button" class="measure-btn measure-btn-done" onClick={share}>
            {tr('qrpass.share')}
          </button>
        </div>
      </div>
    </div>
  )
}
